import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteFileAttachment } from '../../FileAttachment';
import { useDeleteFile } from '../mutations';
import { useFile } from '../queries';
import {
  ClickAnchor,
  ClickMenuContainer,
  ClickMenuItem,
  DownloadFileMenuItemOption,
  ElementsButton,
  Icon,
  Modal,
  openNotification,
  ViewFileMenuItemOption,
} from '../../UI';
import { File } from '../../generated/graphql';
import { downloadFile, type DownloadFileFailure } from '../util';

export interface FileOptionsProps {
  allowDeleteFile?: boolean;
  file: File;
  tenantID: string;
  onDelete?: () => void;
  fileAttachmentID?: string;
  onCancelDelete?: () => void;
  onBeforeDeleteFile?: () => Promise<(() => Promise<void>) | void>;
  onDeleteRollbackFailure?: () => void;
  onDeleteSuccess?: () => void;
  onDownloadFailure?: (failure: DownloadFileFailure) => void;
  shouldRefreshDownloadURL?: boolean;
  showPrimaryDownloadAction?: boolean;
  children?: React.ReactNode;
  itemID: string | undefined | null;
}

export const FileOptions = ({
  allowDeleteFile,
  file,
  tenantID,
  onDelete,
  onCancelDelete,
  onBeforeDeleteFile,
  onDeleteRollbackFailure,
  onDeleteSuccess,
  onDownloadFailure,
  shouldRefreshDownloadURL = true,
  showPrimaryDownloadAction,
  fileAttachmentID,
  itemID,
}: FileOptionsProps) => {
  const { name, createdBy } = file;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreparingDelete, setIsPreparingDelete] = useState(false);
  const { t: tFile } = useTranslation('file');
  const { file: refreshedFile, fileQuery } = useFile(tenantID, file.id, { enabled: false, retry: false });
  const { deleteFile, deleteFileMutation, reconcileDeletedFileCaches } = useDeleteFile(
    allowDeleteFile ? tenantID : undefined
  );
  const { deleteFileAttachment, deleteFileAttachmentMutation } = useDeleteFileAttachment(itemID);
  const downloadURL = shouldRefreshDownloadURL ? refreshedFile?.downloadURL ?? '' : file.downloadURL ?? '';
  const viewFileDisabled = shouldRefreshDownloadURL
    ? fileQuery.isFetching || fileQuery.isError || !downloadURL
    : !downloadURL;
  const downloadFileDisabled = shouldRefreshDownloadURL
    ? fileQuery.isFetching || isDownloading
    : isDownloading || !downloadURL;
  const isDeleting = isPreparingDelete || deleteFileMutation.isLoading || deleteFileAttachmentMutation.isLoading;
  const downloadFreshFile = async () => {
    setIsDownloading(true);
    try {
      const freshFileQuery = shouldRefreshDownloadURL ? await fileQuery.refetch() : undefined;
      const freshURL = shouldRefreshDownloadURL ? freshFileQuery?.data?.downloadURL : file.downloadURL;
      const downloadResult =
        shouldRefreshDownloadURL && !freshFileQuery?.isSuccess
          ? ({ ok: false, reason: 'request-failed' } as const)
          : await downloadFile(name, freshURL);

      if (!downloadResult.ok) {
        onDownloadFailure?.(downloadResult);
        openNotification({
          type: 'error',
          description: tFile('error-unable-to-download'),
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (deleteFileMutation.isSuccess || deleteFileAttachmentMutation.isSuccess) {
      onDeleteSuccess?.();
      setIsOpenModal(false);
    }
  }, [deleteFileAttachmentMutation.isSuccess, deleteFileMutation.isSuccess]);

  return (
    <>
      {showPrimaryDownloadAction && (
        <ElementsButton
          className="mr-2"
          disabled={isDownloading}
          icon="download"
          isLoading={isDownloading}
          label={tFile('download-file-label')}
          onClick={() => void downloadFreshFile()}
          theme="text"
          type="button"
        />
      )}
      <ClickAnchor
        inPortal={false}
        onOpen={() => {
          if (shouldRefreshDownloadURL) void fileQuery.refetch();
        }}
        placement="bottom-start"
        popperJSPreventOverflowOptions={{ padding: 24 }}
        floatingElement={({ setOpen }) => (
          <ClickMenuContainer className="mr-[-8px] !w-[192px]">
            <ViewFileMenuItemOption disabled={viewFileDisabled} downloadURL={downloadURL} fileName={name} />
            {!showPrimaryDownloadAction && (
              <DownloadFileMenuItemOption
                disabled={downloadFileDisabled}
                downloadURL={downloadURL}
                fileName={name}
                onDownload={downloadFreshFile}
              />
            )}
            {(allowDeleteFile || fileAttachmentID) && (
              <ClickMenuItem
                onClick={() => {
                  setOpen(false);
                  onDelete?.();
                  setIsOpenModal(true);
                }}
              >
                <span className="text-lg font-medium text-white">{tFile('delete-file-label')}</span>
                <Icon icon="delete" className="text-darkMode-danger" />
              </ClickMenuItem>
            )}
          </ClickMenuContainer>
        )}
      >
        <Icon icon="moreHoriz" className="file-options-icon h-6 w-6 hover:text-palette-600" />
        <span className="sr-only">{tFile('file-options-label')}</span>
      </ClickAnchor>
      <Modal
        className="fixed inset-x-0 bottom-16 right-16 z-100 mr-0 w-[542px]"
        cancel={() => {
          onCancelDelete?.();
          setIsOpenModal(false);
        }}
        isOpen={isOpenModal}
        title={tFile('delete-file-modal-title-delete-document', {
          fileName: name,
        })}
        bodyText={tFile('delete-file-modal-body-delete-document', {
          userName: `${createdBy.givenName} ${createdBy.familyName}`,
        })}
        okText={tFile('delete-file-modal-confirm-delete-document')}
        onOk={async () => {
          if (allowDeleteFile) {
            setIsPreparingDelete(true);
            let rollbackBeforeDelete: (() => Promise<void>) | undefined;
            let deleteAttempted = false;
            try {
              const beforeDeleteResult = await onBeforeDeleteFile?.();
              rollbackBeforeDelete = typeof beforeDeleteResult === 'function' ? beforeDeleteResult : undefined;
              deleteAttempted = true;
              await deleteFile.mutateAsync({ id: file.id, tenantID });
            } catch {
              if (deleteAttempted) {
                const canonicalFileQuery = await fileQuery.refetch();
                if (canonicalFileQuery.isError || !canonicalFileQuery.isSuccess) {
                  onDeleteRollbackFailure?.();
                  openNotification({
                    type: 'error',
                    description: tFile('error-unable-to-restore-file-association'),
                  });
                } else if (canonicalFileQuery.data) {
                  try {
                    await rollbackBeforeDelete?.();
                    openNotification({
                      type: 'error',
                      description: tFile('error-unable-to-delete'),
                    });
                  } catch {
                    onDeleteRollbackFailure?.();
                    openNotification({
                      type: 'error',
                      description: tFile('error-unable-to-restore-file-association'),
                    });
                  }
                } else {
                  reconcileDeletedFileCaches({ id: file.id, tenantID });
                  onDeleteSuccess?.();
                  setIsOpenModal(false);
                }
              } else {
                openNotification({
                  type: 'error',
                  description: tFile('error-unable-to-delete'),
                });
              }
            } finally {
              setIsPreparingDelete(false);
            }
          } else if (fileAttachmentID) {
            deleteFileAttachment.mutate({ id: fileAttachmentID, tenantID });
          }
        }}
        okLoading={isDeleting}
        cancelDisabled={isDeleting}
        okHTMLType="button"
        okThemeType="danger"
      />
    </>
  );
};
